import { useMemo } from 'react';
import { FilterDropdown } from '../../../../ui/FilterDropdown/FilterDropdown';
import { AppliedFilters } from '../../../../ui/AppliedFilters/AppliedFilters';
import DatePicker from '../../../../ui/DatePicker/DatePicker';
import { severityOptions as defaultSeverityOptions } from './MonitoringFiltersSection.constants';
import { MonitoringFiltersSectionProps } from './MonitoringFiltersSection.types';

export function MonitoringFiltersSection({
  selectedEventType,
  onEventTypeChange,
  selectedSeverity,
  onSeverityChange,
  selectedUser,
  onUserChange,
  selectedDateRange,
  onDateRangeChange,
  onResetFilters,
  eventTypeOptions = [],
  eventTypesLoading = false,
  userOptions = [],
  usersLoading = false,
  severityOptions,
}: MonitoringFiltersSectionProps) {
  const resolvedSeverityOptions = severityOptions ?? defaultSeverityOptions;
  const hasActiveFilters = useMemo(() => {
    return (
      selectedEventType.length > 0 ||
      selectedSeverity.length > 0 ||
      selectedUser.length > 0 ||
      selectedDateRange !== null
    );
  }, [selectedEventType, selectedSeverity, selectedUser, selectedDateRange]);

  return (
    <div className="flex items-center gap-2 w-full">
      <AppliedFilters />

      <div className="flex-1 min-w-0">
        <FilterDropdown
          placeholder={eventTypesLoading ? 'Loading...' : 'Event Type'}
          options={eventTypeOptions}
          selectedValues={selectedEventType}
          onSelectionChange={onEventTypeChange}
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        <FilterDropdown
          placeholder="Severity"
          options={resolvedSeverityOptions}
          selectedValues={selectedSeverity}
          onSelectionChange={onSeverityChange}
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        <FilterDropdown
          placeholder={usersLoading ? 'Loading...' : 'User'}
          options={userOptions}
          selectedValues={selectedUser}
          onSelectionChange={onUserChange}
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        <DatePicker
          placeholder="Date"
          value={
            selectedDateRange
              ? {
                  startDate: new Date(selectedDateRange.startDate),
                  endDate: new Date(selectedDateRange.endDate),
                }
              : null
          }
          onDateRangeChange={(range) => {
            if (range.startDate && range.endDate) {
              onDateRangeChange({
                startDate: range.startDate.toISOString(),
                endDate: range.endDate.toISOString(),
              });
            } else {
              onDateRangeChange(null);
            }
          }}
          className="w-full"
        />
      </div>

      <button
        className={`flex items-center gap-2 px-3 py-2 flex-shrink-0 transition-colors ${
          hasActiveFilters ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
        }`}
        onClick={hasActiveFilters ? onResetFilters : undefined}
        disabled={!hasActiveFilters}
      >
        <span
          className={`text-xs leading-5 font-medium ${
            hasActiveFilters ? 'text-purple-800' : 'text-gray-200'
          }`}
        >
          Reset Filters
        </span>
      </button>
    </div>
  );
}
